<!--
Copyright (c) 2020 ForgeRock. All rights reserved.

This software may be modified and distributed under the terms
of the MIT license. See the LICENSE file for details.
-->

<template>
    <div>
    <ValidationProvider :rules="validateRules" :vid="id" :name="fieldNameForError || label" v-slot="validationContext">
            <div class="ies-label-group-alt" ref="floatingLabelGroup" :class="fieldName">
                <label class="ies-label" :for="id" v-html="label"></label>
                <input :type="inputType"
                        v-model="inputValue"
                        :id="id"
                        data-vv-validate-on="blur|submit"
                        ref="input"
                        :placeholder="placeholder"
                        :name="fieldName"
                        :disabled="disabled"
                        :autocomplete="autocompleteValue"
                        @click="handleClick"
                        @focus="handleFocus"
                        :aria-describedby="'emptyField-errorMsg-' + fieldName"
                        :aria-required="String(validateRules).includes('required')"
                        :class="['ies-input', {'invalid' :  validationContext.errors.length > 0 && showErrorState}]"
                    />

                <div class="fr-validation-requirements text-left" :id="'emptyField-errorMsg-' + fieldName" aria-live="polite">
                    <!-- Error messages have priority over warning messages -->
                    <span class="text-danger" v-if="validationContext.errors.length > 0 && showErrorState">
                        <span v-if="typeof validationContext.errors[0] === 'object' && validationContext.errors[0].__html" v-html="validationContext.errors[0].__html"></span>
                        <span v-else>{{ validationContext.errors[0] }}</span>
                    </span>
                    <!-- Warning messages show when there are no errors -->
                    <span class="text-warning" v-else-if="showWarning && warningMessage" v-html="warningMessage"></span>
                </div>
            </div>
        </ValidationProvider>

    </div>
</template>

<script>
import _ from 'lodash';

/**
 * @description Input with a floating label in the center, this will move when a user types into the input (example can be found on the default login page)
 *
 **/
export default {
    name: 'Floating-Label-Input',
    components: {
    },
    props: {
        label: String,
        type: String,
        autofocus: String,
        fieldName: String,
        fieldNameForError: { type: String, default: '' },
        validateRules: {
            type: [String, Object]
        },
        reveal: Boolean,
        disabled: { type: Boolean, default: false },
        showErrorState: { type: Boolean, default: true },
        defaultValue: { required: false },
        value: {
            type: String,
            default () { return ''; }
        },
        placeholder: { type: String, default: '' },
        autocompleteValue: { type: String, default: '' },
        warningMessage: { type: String, default: '' },
        showWarning: { type: Boolean, default: false }
    },
    data () {
        return {
            inputValue: this.value,
            id: null,
            floatLabels: false,
            hideLabel: true,
            inputType: this.type,
            show: true
        };
    },
    created () {
    },
    beforeMount () {
        this.id = 'floatingLabelInput' + this._uid;
    },
    mounted () {
        /* istanbul ignore next */
        _.delay(_.bind(() => {
            if (navigator.userAgent.indexOf('Edge') >= 0) {
                if (document.getElementById(`${this.id}`).value.length) {
                    this.floatLabels = true;
                    this.inputValue = document.getElementById(`${this.id}`).value;
                }
            } else if (navigator.userAgent.indexOf('Chrome') >= 0) {
                if (document.querySelectorAll(`#${this.id}:-webkit-autofill`).length > 0) {
                    this.floatLabels = true;
                }
            }
            const clientIdKey = this.getQueryStringValue('client_id');
            const setPasswordKey = this.getQueryStringValue('setPassword');
            const userNameParam = this.getQueryStringValue('userName');
            const loginUserName = document.getElementsByClassName('ies-input');
            if (loginUserName.length > 0 && loginUserName.uid && loginUserName.uid.id) {
                const loginUserNameId = loginUserName.uid.id;
                const inputElement = document.querySelector('#' + loginUserNameId);
                if (clientIdKey === 'PVS-Client' && clientIdKey !== null && setPasswordKey !== null &&
                setPasswordKey === 'true' && userNameParam !== null) {
                    inputElement.value = userNameParam;
                }
            }
            this.hideLabel = false;
        }, this), 400);

        if (this.defaultValue) {
            this.inputValue = this.defaultValue;
        }

        // Browser consistent focus fix
        if (this.autofocus === 'true') {
            this.$refs.input.focus();
        }
    },
    methods: {
        revealText: function () {
            if (this.inputType === 'password') {
                this.inputType = 'text';
                this.show = false;
            } else {
                this.inputType = 'password';
                this.show = true;
            }
        },
        handleFocus () {
            this.$emit('focus', event);
        },
        getQueryStringValue (name) {
            let url = window.location.href;
            name = name.replace(/[[]/, '\\["').replace(/[\]]/, '\\]');
            let regexS = '[\\?&]' + name + '=([^&#]*)';
            let regex;
            if (name.toLowerCase() === 'username') {
                regex = new RegExp(regexS, 'i'); // Add 'i' flag for case insensitivity
            } else {
                regex = new RegExp(regexS);
            }
            let results = regex.exec(url);
            return results == null ? null : decodeURIComponent(results[1]);
        },
        handleClick () {
            this.$emit('click', event);
        }
    },
    watch: {
        inputValue: function (newVal) {
            this.floatLabels = newVal.length > 0;
            this.$emit('input', newVal);
        },
        value: function () {
            this.inputValue = this.value;
        }
    }
};
</script>

<style lang="scss" scoped>
    .form-label-password.form-label-group {
        display: flex;

        .form-control {
            flex-grow: 1;
            border-bottom-right-radius: 0;
            border-top-right-radius: 0;
        }

        .input-group-append {
            flex-grow: 1;

            button {
                border-bottom-left-radius: 0;
                border-top-left-radius: 0;
                background-color: $input-bg;
                border-color: $input-border-color;
                color:$input-btn-color;
            }
            button:hover {
              color: $input-btn-active-color;
            }

        }
    }

    .form-label-group {
        position: relative;
        margin-bottom: 1rem;
    }

    //-->
    // Bootstrap Floating Labels
    // https://getbootstrap.com/docs/4.0/examples/floating-labels/
    //-->
    .form-label-group > input,
    .form-label-group > label,
    .form-label-group > .input-group > input,
    .form-label-group > .input-group > label {
        padding: $input-btn-padding-y;
    }

    .form-label-group > label,
    .form-label-group > .input-group > label {
        position: absolute;
        top: 0;
        left: 0;
        display: block;
        width: 100%;
        margin-bottom: 0; /* Override default `<label>` margin */
        line-height: 1.5;
        color: $label-color;
        border: 1px solid transparent;
        border-radius: .25rem;
        transition: all .1s ease-in-out;
    }

    .form-label-group input::-webkit-input-placeholder {
        color: transparent;
    }

    .form-label-group input:-ms-input-placeholder {
        color: transparent;
    }

    .form-label-group input:-ms-input-placeholder {
        color: transparent;
    }

    .form-label-group input::-moz-placeholder {
        color: transparent;
    }

    .form-label-group input::placeholder {
        color: transparent;
    }

    .form-label-group .polyfillPlaceholder {
        padding-top: $input-btn-padding-y + $input-btn-padding-y * (2 / 3);
        padding-bottom: $input-btn-padding-y / 3;
        color: $input-color;
    }

    .form-label-group .polyfillPlaceholder ~ label {
        padding-top: $input-btn-padding-y / 3;
        padding-bottom: $input-btn-padding-y / 3;
        font-size: 12px;
        color: $label-color;
    }
    .form-control.error,
.was-validated .form-control:invalid, .form-control.is-invalid {
    border-color: #E84444;
    box-shadow: inset 0 1px 1px rgba(0,0,0,.075), 0 0 8px rgba(232,68,68,.6);
}
   .placeholder-label {
    color: grey !important;
    font-family: 'Open Sans', sans-serif !important;
}
input[type="text"]:disabled {
background-color:lightgray !important;
}

</style>
